/**
 * useValidation - Custom hook for scenario validation
 *
 * Validates all required fields before optimization can run.
 * Per coding standards: "Never use silent fallbacks to default values.
 * When required data is missing, always fail loudly."
 */

import { useMemo } from 'react';

/**
 * Validation rule definitions
 * Each rule returns true if valid, false if invalid
 */
const createValidationRules = (formState) => ({
    // Tariff is always required (unless off-grid)
    tariff: {
        label: 'Electric Tariff',
        section: 'tariff',
        isValid: () => {
            if (formState.offGrid) return true;
            return !!(formState.customTariffData || formState.selectedTariffData);
        },
        message: 'Select an electric tariff or enter manual rates',
    },

    // Voltage level required when using custom/manual tariff for CA DER calculations
    voltageLevel: {
        label: 'Voltage Level',
        section: 'tariff',
        isValid: () => {
            if (formState.offGrid) return true;
            // Only required when using manual rate or custom tariff
            if (!formState.useManualRate && !formState.customTariffData && !formState.selectedTariffData) return true;
            // Check if voltage level is set in either customTariffData or selectedTariffData
            const voltageLevel = formState.customTariffData?.voltage_level ||
                                 formState.selectedTariffData?.voltage_level ||
                                 formState.selectedTariffData?.rate_data?.voltage_level;
            return !!voltageLevel;
        },
        message: 'Select voltage level (Secondary, Primary, or Transmission)',
    },

    // Generator fuel type required when generator is enabled
    generatorFuelType: {
        label: 'Generator Fuel Type',
        section: 'generator',
        isValid: () => {
            if (!formState.useGenerator) return true;
            return !!formState.generatorFuelType;
        },
        message: 'Select fuel type for generator (Diesel, Natural Gas, or Propane)',
    },

    // Blended energy rate required when using blended rate mode
    blendedEnergyRate: {
        label: 'Blended Energy Rate',
        section: 'tariff',
        isValid: () => {
            if (formState.offGrid) return true;
            if (!formState.useManualRate) return true;
            if (formState.rateStructureType !== 'blended') return true;
            const rate = parseFloat(formState.blendedEnergyRate);
            return !isNaN(rate) && rate > 0;
        },
        message: 'Enter a blended energy rate greater than $0/kWh',
    },

    // TOU period rates required when using TOU rate mode
    touPeriodRates: {
        label: 'TOU Period Rates',
        section: 'tariff',
        isValid: () => {
            if (formState.offGrid) return true;
            if (!formState.useManualRate) return true;
            if (formState.rateStructureType !== 'tou') return true;
            // All periods must have energy rate > 0
            const periods = formState.touPeriods || [];
            if (periods.length === 0) return false;
            return periods.every(p => {
                const rate = parseFloat(p.energyRate);
                return !isNaN(rate) && rate > 0;
            });
        },
        message: 'Enter energy rates for all TOU periods',
    },

    // Load profile required (annual kWh or hourly data)
    loadProfile: {
        label: 'Load Profile',
        section: 'site',
        isValid: () => {
            // Check if we have annual kWh or uploaded load data
            // This is checked elsewhere in the form, so we assume valid for now
            // The scenario builder will catch this if missing
            return true;
        },
        message: 'Upload load profile or enter annual energy consumption',
    },

    // Gas tariff required when using natural gas generator
    gasTariff: {
        label: 'Gas Tariff',
        section: 'generator',
        isValid: () => {
            if (!formState.useGenerator) return true;
            if (formState.generatorFuelType !== 'natural_gas') return true;
            // Need either gas tariff or manual fuel cost
            const hasTariff = !!formState.gasTariffId;
            const hasFuelCost = parseFloat(formState.generatorFuelCostPerMmbtu) > 0;
            return hasTariff || hasFuelCost;
        },
        message: 'Select a gas tariff or enter fuel cost for natural gas generator',
    },
});

/**
 * Hook to validate scenario form state
 *
 * @param {Object} formState - Form state from useScenarioForm
 * @returns {Object} Validation state and helpers
 */
export function useValidation(formState) {
    const validationRules = useMemo(
        () => createValidationRules(formState),
        [formState]
    );

    const validationResults = useMemo(() => {
        const results = {};
        for (const [key, rule] of Object.entries(validationRules)) {
            results[key] = {
                ...rule,
                valid: rule.isValid(),
            };
        }
        return results;
    }, [validationRules]);

    const invalidFields = useMemo(() => {
        return Object.entries(validationResults)
            .filter(([_, result]) => !result.valid)
            .map(([key, result]) => ({
                key,
                label: result.label,
                section: result.section,
                message: result.message,
            }));
    }, [validationResults]);

    const isValid = useMemo(() => invalidFields.length === 0, [invalidFields]);

    // Group invalid fields by section for UI display
    const invalidBySection = useMemo(() => {
        const grouped = {};
        for (const field of invalidFields) {
            if (!grouped[field.section]) {
                grouped[field.section] = [];
            }
            grouped[field.section].push(field);
        }
        return grouped;
    }, [invalidFields]);

    return {
        isValid,
        invalidFields,
        invalidBySection,
        validationResults,
        // Helper to check specific field
        isFieldValid: (fieldKey) => validationResults[fieldKey]?.valid ?? true,
        // Get message for specific field
        getFieldMessage: (fieldKey) => validationResults[fieldKey]?.message,
    };
}

export default useValidation;
